/**
 * The only DOM-aware module in the package.
 *
 * Delegated listeners on one render root, so it works over any renderer that
 * emits the anchor attributes — the string renderer today, @Codex's React
 * interpreter tomorrow — without a second permission boundary. Everything it
 * decides, it decides by calling `edit.ts`; this file contributes events,
 * focus, a caret and a live region, and no policy of its own.
 *
 * **Native history is taken, not shared.** The Chromium traces in
 * `spikes/inline-composition/TRACES-chromium.md` show one document-wide undo
 * stack that walks into modules the user is not editing, per character, with no
 * focus change to show where the edit landed. Sharing that stack is not an
 * option, so:
 *
 *  1. the platform chord is intercepted on keydown/capture,
 *  2. a `beforeinput` whose inputType starts `history` is cancelled, and
 *  3. a committed field is REPLACED with a fresh element, so any native entry
 *     that survived both points at a detached node and cannot rewrite anything.
 *
 * Three layers because interception alone is a bet on every browser firing the
 * event cancelably, and only Chromium has been traced.
 */

import { isProse } from '@morph/spec';
import type { IR } from './ir.js';
import { formatValue } from './render-html.js';
import {
  ANCHOR_ATTR, OWNS_EDITING_ATTR, handleOf, permitGesture, readAnchor, refuse,
  resolveEdit, scopeOf, transactionOf,
  type BoundValues, type EditAnchor, type EditTransaction, type GrantSet,
  type ObjectHandle, type Refusal, type UndoScopeId, type WriteGrant,
  type WriteOutcome,
} from './edit.js';

/** What a repaint is FOR, so "the data changed" and "the layout should change"
 *  stay separate decisions. `drift` is never applied by the host: a different
 *  spec is offered through the existing offer/preview/keep loop, because a
 *  layout that changes under the cursor is the failure the loop exists to
 *  prevent. */
export type RepaintReason = 'value' | 'structure' | 'drift';

const TIER: Record<RepaintReason, number> = { value: 0, structure: 1, drift: 2 };

export interface RenderPass {
  readonly ir: IR;
  readonly lens: string;
  readonly epoch: number;
  readonly grants: GrantSet;
  /** The objects this pass painted — the same array handed to the renderer.
   *  The revert target and the `before` half of a commit are read from here, so
   *  the DOM is never the authority for either. */
  readonly objects: readonly BoundValues[];
  /**
   * The other named sets the pass painted, when a collection asked for one.
   *
   * EVERY object on screen has to be indexed here, not just the subject. A
   * read-write binding inside a `siblings` collection emits an editable
   * element like any other, and if its object is not in this index the session
   * records `opening` as undefined — so Escape, or any refused commit, replaces
   * a real value with an empty string. Found by @Codex.
   *
   * Same array the renderer got, for the same reason as `objects`: the DOM is
   * never the authority for a revert.
   */
  readonly sources?: Readonly<Partial<Record<string, readonly BoundValues[]>>>;
}

export interface EditPolicy {
  /** Native history is taken, not shared. See this file's header. */
  readonly ownUndo: boolean;
  /** On commit the editable element is replaced from the store value. */
  readonly replaceOnCommit: boolean;
  /** A structural repaint never runs while a field session is open. */
  readonly deferRepaintWhileEditing: boolean;
  /** Long enough to swallow a burst of commits, short enough that a new board
   *  column appears while the user still expects it. A prior with no evidence
   *  behind it: it belongs in the parameter registry beside the rest of the feel
   *  budget once task 0009 has measured numbers, and it is here rather than
   *  there only because `packages/generation` is not a dependency of the
   *  runtime. */
  readonly coalesceRepaintMs: number;
  readonly commitOn: readonly ('blur' | 'enter')[];
  readonly cancelOn: readonly 'escape'[];
}

export const DEFAULT_EDIT_POLICY: EditPolicy = {
  ownUndo: true,
  replaceOnCommit: true,
  deferRepaintWhileEditing: true,
  coalesceRepaintMs: 120,
  commitOn: ['blur', 'enter'],
  cancelOn: ['escape'],
};

/**
 * The store, as the write path uses it.
 *
 * A structural subset of the eventual `MorphStore` — @Codex's lane under 0002 —
 * so the runtime can hold the boundary without depending on a package it does
 * not own. There is no `set(id, key, value)` anywhere in it: binding direction
 * is enforced by there being nothing else to call.
 */
export interface EditStore {
  /** A lens registers its grants when installed, so an intent whose grant id is
   *  not in the registered set fails a LOOKUP rather than a comparison. */
  installLens(lens: string, grants: GrantSet): void;
  uninstallLens(lens: string): void;
  apply(txn: EditTransaction): Promise<WriteOutcome>;
  undo(scope: UndoScopeId): Promise<WriteOutcome | undefined>;
  redo(scope: UndoScopeId): Promise<WriteOutcome | undefined>;
}

export interface EditHostOptions {
  readonly root: Element;
  readonly store: EditStore;
  /** The host's own live region. Polite: a refusal must not interrupt and must
   *  not steal focus — the suggestion surface's never-steal rule, generalised
   *  to every message the runtime produces. */
  readonly status: Element;
  /** Asked for, never taken. The app decides when to re-render, because the app
   *  owns the data it would re-render from. */
  readonly onRepaintRequest: (why: RepaintReason) => void;
  readonly onRefusal?: (r: Refusal) => void;
  readonly policy?: Partial<EditPolicy>;
}

export interface FieldSession {
  readonly anchor: EditAnchor;
  readonly grant: WriteGrant;
  readonly startedAt: number;
  /** Store value when the session opened. The revert target and the `before`
   *  half of the commit — the DOM is never the authority for either. */
  readonly opening: unknown;
  /** IME in flight. Never commit, never repaint mid-composition. */
  readonly composing: boolean;
}

/** How the previous field session ended — the record the focus-restore
 *  routing reads to choose an offset (task 0163). */
export interface SessionEnd {
  readonly anchor: EditAnchor;
  /** Text offset of the caret at the last input, when one was recorded. */
  readonly caret?: number;
  /** 'enter' finished the value; 'blur' and 'refocus' are how a repaint or a
   *  focus steal ends a session mid-typing; 'api' is a programmatic commit. */
  readonly why: 'enter' | 'blur' | 'refocus' | 'api';
}

export interface EditHost {
  /** Read-and-clear: how the previous session ended, at most once. */
  takeLastEnded(): SessionEnd | undefined;
  /** Called after every render with the pass that painted the DOM. */
  bind(pass: RenderPass): void;
  readonly epoch: number;
  /** No open session, no composition, no selection inside the root. */
  isQuiescent(): boolean;
  session(): FieldSession | undefined;
  /** Restores focus and caret after a structural repaint by re-resolving the
   *  anchor — handles are stable per object per session, ordinals are not. */
  restore(anchor: EditAnchor, offset: number): boolean;
  undo(): Promise<WriteOutcome | undefined>;
  redo(): Promise<WriteOutcome | undefined>;
  /** A commit that happened somewhere else — another lens, another tab, a
   *  server under 0005. Routed through the same deferral so it cannot repaint
   *  under an open field session. */
  notify(why: RepaintReason): void;
  destroy(): void;
}

interface LiveSession {
  anchor: EditAnchor;
  grant: WriteGrant;
  startedAt: number;
  opening: unknown;
  composing: boolean;
  /** Something was actually typed. A focus-and-leave must not write. */
  dirty: boolean;
  /** Text offset of the collapsed caret inside `el`, recorded on every input
   *  — task 0163: a session ended by a repaint needs to say where the person
   *  WAS, or the restore throws the caret to the end and mid-sentence typing
   *  arrives as two fragments. */
  caret?: number;
  el: Element;
}

const isEditable = (el: Element | null): el is Element =>
  !!el && el.getAttribute('contenteditable') === 'true';

/** Inside a surface that owns its own editing. See `OWNS_EDITING_ATTR`. */
const foreign = (el: Element | null): boolean => !!el?.closest(`[${OWNS_EDITING_ATTR}]`);

export function attachEditHost(opts: EditHostOptions): EditHost {
  const policy: EditPolicy = { ...DEFAULT_EDIT_POLICY, ...opts.policy };
  const { root, store, status } = opts;
  const doc = root.ownerDocument;
  const win = doc.defaultView;

  // The host owns its live region's contract; an app that forgot the attributes
  // would produce refusals that are visible only to people who can see.
  if (!status.getAttribute('role')) status.setAttribute('role', 'status');
  if (!status.getAttribute('aria-live')) status.setAttribute('aria-live', 'polite');

  let pass: RenderPass | undefined;
  let values = new Map<ObjectHandle, BoundValues>();
  let live: LiveSession | undefined;
  let lastEnded: SessionEnd | undefined;
  let pending: RepaintReason | undefined;
  let timer: ReturnType<typeof setTimeout> | undefined;

  /* ------------------------------------------------------------ announcing */

  const announce = (r: Refusal): void => {
    // The region has to be IN the accessibility tree before the insertion, not
    // after it. A host that ships its status element `hidden` and reveals it in
    // its own `onRefusal` — which is what the demo did — inserts into a
    // display:none subtree and then unhides it with the content already in
    // place, and that is the one sequence assistive tech does not announce. The
    // refusal was drawn on screen and never spoken. The host owns this element's
    // contract, the same way it owns `role` and `aria-live` above.
    if (status.hasAttribute('hidden')) status.removeAttribute('hidden');
    // A live region only announces what CHANGED, so the same refusal twice in a
    // row is silent if the text is merely reassigned. Replacing the node makes
    // every refusal an insertion, which is what assistive tech watches for.
    while (status.firstChild) status.removeChild(status.firstChild);
    const line = doc.createElement('span');
    line.textContent = r.message;
    status.appendChild(line);
    opts.onRefusal?.(r);
  };

  /* -------------------------------------------------------------- the DOM */

  /** Replace an element with a fresh one carrying `text`.
   *
   *  Containment, not cosmetics: a native history entry that survived
   *  interception now references a node that is no longer in the document, so
   *  it cannot rewrite live content. */
  const replaceWith = (el: Element, text: string): Element => {
    const fresh = el.cloneNode(false) as Element;
    fresh.textContent = text;
    const focused = doc.activeElement === el;
    el.parentNode?.replaceChild(fresh, el);
    if (focused && typeof (fresh as HTMLElement).focus === 'function') {
      (fresh as HTMLElement).focus();
      caretTo(fresh, text.length);
    }
    return fresh;
  };

  /** Where the collapsed caret sits inside `el`, as a text offset — the
   *  inverse of `caretTo`, measured through a range so it survives the
   *  browser splitting text nodes. `undefined` when the selection is not a
   *  collapsed point inside the element. */
  const caretOffsetIn = (el: Element): number | undefined => {
    const sel = win?.getSelection?.();
    if (!sel || sel.rangeCount === 0 || !sel.isCollapsed) return undefined;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.startContainer)) return undefined;
    try {
      const before = doc.createRange();
      before.selectNodeContents(el);
      before.setEnd(range.startContainer, range.startOffset);
      return before.toString().length;
    } catch { return undefined; }
  };

  const onInput = (e: Event): void => {
    if (!live) return;
    const el = e.target as Element | null;
    if (el !== live.el && !(el && live.el.contains(el))) return;
    const at = caretOffsetIn(live.el);
    if (at !== undefined) live.caret = at;
  };

  const caretTo = (el: Element, offset: number): boolean => {
    const sel = win?.getSelection?.();
    if (!sel) return false;
    try {
      const range = doc.createRange();
      const first = el.firstChild;
      if (first && first.nodeType === 3) {
        range.setStart(first, Math.min(offset, first.textContent?.length ?? 0));
      } else {
        range.setStart(el, Math.min(offset, el.childNodes.length));
      }
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return true;
    } catch {
      // Selection is the part of the DOM most likely to differ between hosts,
      // and failing to place a caret must not fail the edit.
      return false;
    }
  };

  /**
   * What the user typed, INCLUDING the line breaks.
   *
   * This was `el.textContent`, and `textContent` does not see element
   * boundaries. Pressing Enter in a contenteditable builds
   * `Alpha.<div>Beta.</div>` in Chromium, and `textContent` reads that back as
   * `"Alpha.Beta."` — measured, not inferred. So a note with two paragraphs was
   * stored as one run-on string with the words at the boundary jammed together,
   * and there was no way to write a note of any length. Task 0044 called this
   * "text that renders correctly" and it was one property read.
   *
   * `innerText` is the reader that respects rendered line breaks:
   * `"Alpha.\nBeta."` from the same DOM. It costs a layout flush, which is the
   * right price for the only reader that answers the question being asked.
   *
   * The fallback is not defensive noise. **jsdom does not implement
   * `innerText`** — it has no layout, so it cannot — and every unit test in this
   * package runs there. Without the fallback the suite would read `undefined`
   * from every field and the tests would be exercising a host nobody ships.
   */
  const textOf = (el: Element): string => {
    const rendered = (el as HTMLElement).innerText;
    return typeof rendered === 'string' ? rendered : el.textContent ?? '';
  };

  /** Does the live field hold prose? Read from the IR node the anchor names,
   *  so it is the same answer the renderer got when it drew the field. */
  const prose = (s: LiveSession | undefined): boolean => {
    const role = s && pass ? pass.ir.nodes[s.anchor.node]?.role : undefined;
    return role !== undefined && isProse(role);
  };

  /* ------------------------------------------------------------- sessions */

  const openSession = (el: Element, anchor: EditAnchor): LiveSession | undefined => {
    if (!pass) return undefined;
    const permitted = permitGesture({
      ir: pass.ir, grants: pass.grants, anchor, epoch: pass.epoch,
      // Opening a session is not yet a write, so the gesture is the one this
      // element affords. A refusal here is silent: announcing on focus would
      // scold a user for looking at a read-only cell.
      gesture: { kind: 'text', inputType: 'insertText', text: '' },
    });
    if (!permitted.ok) return undefined;
    const opening = values.get(anchor.object)?.[permitted.role];
    live = {
      anchor, grant: permitted.grant, startedAt: Date.now(),
      opening, composing: false, dirty: false, el,
    };
    return live;
  };

  /** Close without writing. A repaint deferred while the session was open is
   *  released here rather than being lost. */
  const dropSession = (): void => {
    live = undefined;
    maybeFlush();
  };

  /** Put the store's value back. Never the DOM's — the DOM is what we are
   *  distrusting. */
  const revert = (s: LiveSession): void => {
    if (s.el.isConnected) replaceWith(s.el, formatValue(s.opening));
  };

  const commit = async (why: SessionEnd['why'] = 'api'): Promise<WriteOutcome | undefined> => {
    const s = live;
    if (!s || !pass) return undefined;
    // A composition in flight is not a value yet; committing mid-IME writes a
    // half-composed string and drops the rest.
    if (s.composing) return undefined;
    // Recorded BEFORE the session dies, consumed once by whoever routes the
    // focus restore — task 0163. `why` is the half a caret cannot carry: on
    // Enter the person finished the value and end-of-value is where they
    // continue; on a blur or refocus forced by a repaint they were mid-word,
    // and the recorded offset is the only honest place to put them back.
    lastEnded = { anchor: s.anchor, why, ...(s.caret !== undefined ? { caret: s.caret } : {}) };
    live = undefined;
    if (!s.dirty) { maybeFlush(); return undefined; }   // focused and left alone

    const res = resolveEdit({
      ir: pass.ir, grants: pass.grants, anchor: s.anchor, epoch: pass.epoch,
      gesture: { kind: 'text', inputType: 'insertReplacementText', text: textOf(s.el) },
    });
    if (!res.ok) {
      announce(res.refusal);
      revert(s);
      maybeFlush();
      return { ok: false, refusal: res.refusal };
    }

    const outcome = await store.apply(
      transactionOf([res.intent], res.intent.scope, Date.now()),
    );
    if (!outcome.ok) {
      // Refused three times over: the gesture was cancelled where it could be,
      // the store re-checked against the grants registered at lens install, and
      // the value the user sees comes back from the store rather than staying
      // as whatever the DOM was left holding.
      announce(outcome.refusal);
      revert(s);
      maybeFlush();
      return outcome;
    }

    const patch = outcome.commit.patches.find(
      (p) => p.object === s.anchor.object && p.role === res.intent.role,
    );
    if (!patch) {
      // Committed, but the store reported no patch for the property we wrote.
      // Writing '' into the cell would blank a value on the strength of a
      // store bug, so ask for a repaint and let the store say what is true.
      requestRepaint('value');
      return outcome;
    }
    if (policy.replaceOnCommit) replaceWith(s.el, formatValue(patch.after));
    // A write that CREATED a property changed the collection's shape, so the
    // layout may now have a column it did not have. A write that replaced a
    // value did not, and repainting for it would be churn.
    if (patch.created) requestRepaint('structure');
    maybeFlush();
    return outcome;
  };

  /* -------------------------------------------------------------- repaint */

  const requestRepaint = (why: RepaintReason): void => {
    if (!pending || TIER[why] > TIER[pending]) pending = why;
    maybeFlush();
  };

  const maybeFlush = (): void => {
    if (!pending) return;
    if (policy.deferRepaintWhileEditing && !isQuiescent()) return;
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      // Re-checked, because a field session can open INSIDE the coalescing
      // window. Leaving `pending` set means closing that session releases the
      // repaint rather than losing it.
      if (policy.deferRepaintWhileEditing && !isQuiescent()) return;
      const why = pending;
      pending = undefined;
      if (why) opts.onRepaintRequest(why);
    }, policy.coalesceRepaintMs);
  };

  function isQuiescent(): boolean {
    if (live) return false;
    // The caret is judged by the ACTIVE ELEMENT, not by the selection: a host
    // can leave a collapsed range behind in an element focus has already left,
    // and treating that as "still editing" would stop the lens repainting for
    // the rest of the session. jsdom does exactly this, and so, sometimes, do
    // browsers.
    const active = doc.activeElement;
    if (active && root.contains(active) && active.getAttribute('contenteditable') === 'true') {
      return false;
    }
    // A range the user is dragging out is different: repainting through it
    // destroys a selection they are in the middle of making. `=== false` rather
    // than `!` so a host that does not report collapse fails toward repainting.
    const sel = win?.getSelection?.();
    if (sel && sel.rangeCount > 0 && sel.isCollapsed === false) {
      if (root.contains(sel.getRangeAt(0).commonAncestorContainer)) return false;
    }
    return true;
  }

  /* ---------------------------------------------------------------- scope */

  const focusedScope = (): UndoScopeId => {
    if (!pass) return 'lens:' as UndoScopeId;
    const anchor = live?.anchor ?? readAnchor(doc.activeElement as Element | null);
    return anchor
      ? scopeOf(pass.ir, anchor.node, pass.lens)
      : (`lens:${pass.lens}` as UndoScopeId);
  };

  /* ------------------------------------------------------------ listeners */

  const onFocusIn = (e: Event): void => {
    const el = e.target as Element | null;
    if (!isEditable(el) || foreign(el)) return;
    if (live?.el === el) return;
    if (live) void commit('refocus');
    const anchor = readAnchor(el);
    if (anchor) openSession(el, anchor);
  };

  const onFocusOut = (e: Event): void => {
    if (foreign(e.target as Element | null)) return;
    if (!live || e.target !== live.el) return;
    if (!policy.commitOn.includes('blur')) return;
    void commit('blur');
  };

  /**
   * A chooser changed — the producer for a path that never had one.
   *
   * `EditGesture` has carried `{kind: 'choose', option}` since decision 0007,
   * `resolveEdit` normalises it into a `set-enum` value, the store performs
   * that mutation and the demo's bridge even refuses a TYPED enum with *"this
   * field is chosen from, not typed into"*. Every layer existed except the one
   * that fires, so the generator marked every enum `read` and said so in a
   * comment. This is that layer.
   *
   * Not a text session. A `<select>` has no caret, no composition and no
   * `beforeinput`: the value changes once, atomically, and that IS the commit.
   * So this does not open a `LiveSession` — coupling it to one would make a
   * chooser hold the repaint queue open for a gesture that is already over.
   *
   * **A refusal repaints rather than restoring the old option.** The store is
   * the authority for what the value is, which is this file's rule everywhere
   * else; putting the previous option back from memory would be the DOM
   * asserting a value nobody re-read.
   */
  const onChange = (e: Event): void => {
    const el = e.target as HTMLSelectElement | null;
    if (!el || foreign(el) || !pass || !el.classList.contains('m-choose')) return;

    /**
     * THE FIRST APPLICATION CODE THE GESTURE REACHES — task `0139`.
     *
     * `morph.edit.apply` showed the store commit is ~5ms of a ~70ms edit wall
     * and the repaint ~25ms, leaving ~40ms attributed to nothing. But that
     * wall is measured in Node around `page.selectOption()`, which locates the
     * element, scrolls it into view, runs actionability checks and dispatches
     * before any of this runs — so the 40ms could be the harness rather than
     * the product, and `0139` says explicitly that no change may be aimed at
     * it until that is settled.
     *
     * This mark settles it. `morph.edit.resolve` spans from here to the moment
     * `store.apply` is called, so `wall - resolve - commit - repaint` is what
     * is left for Playwright and for the gaps between them.
     */
    const enteredAt = typeof performance === 'object'
      && typeof performance.now === 'function' ? performance.now() : undefined;

    const anchor = readAnchor(el);
    if (!anchor) {
      // Same case as `onBeforeInput`: an affordance with nothing behind it.
      // There is no gesture to cancel here — it has already happened — so the
      // only honest answer is to say so and let the store redraw the truth.
      announce(refuse('no-grant'));
      requestRepaint('value');
      return;
    }

    const res = resolveEdit({
      ir: pass.ir, grants: pass.grants, anchor, epoch: pass.epoch,
      gesture: { kind: 'choose', option: el.value },
    });
    if (!res.ok) {
      announce(res.refusal);
      requestRepaint('value');
      return;
    }

    /**
     * THE STORE COMMIT, MARKED — task `0139`.
     *
     * `e2e/frame-time.mjs` splits the edit wall at `morph.render.schedule` and
     * finds ~46ms of ~71ms spent BEFORE the repaint begins. That remainder is
     * this `apply` plus the scheduling around it, and nothing separated them:
     * the repaint boundary starts when the repaint starts, not when the write
     * finished.
     *
     * On THIS path and not on `widenEnum`'s, which was where it went first.
     * The harness drives `select.m-choose`, whose change event lands here — so
     * the mark went on a real write path that the measurement never reaches,
     * and the harness printed no commit figure rather than a wrong one. A mark
     * no harness reaches is an affordance with nothing behind it, which is the
     * defect this file's own `onChange` refuses two branches up.
     *
     * Same idiom as `morph.render.schedule` and `morph.ingest.write`, bounded
     * guard included — an entry lives until something clears it and an editing
     * session is thousands of writes. Taken BEFORE the ok/refused branch, so a
     * refusal is measured too: a refused write still costs the frame.
     */
    const applyAt = typeof performance === 'object'
      && typeof performance.now === 'function' ? performance.now() : undefined;
    if (enteredAt !== undefined && applyAt !== undefined
      && typeof performance.measure === 'function') {
      try {
        if (performance.getEntriesByName('morph.edit.resolve', 'measure').length > 8) {
          performance.clearMeasures('morph.edit.resolve');
        }
        performance.measure('morph.edit.resolve', { start: enteredAt, end: applyAt });
      } catch { /* no-op */ }
    }
    void store.apply(transactionOf([res.intent], res.intent.scope, Date.now()))
      .then((outcome) => {
        if (applyAt !== undefined && typeof performance.measure === 'function') {
          try {
            if (performance.getEntriesByName('morph.edit.apply', 'measure').length > 8) {
              performance.clearMeasures('morph.edit.apply');
            }
            performance.measure('morph.edit.apply', { start: applyAt });
          } catch { /* no-op */ }
        }
        if (!outcome.ok) {
          announce(outcome.refusal);
          requestRepaint('value');
          return;
        }
        // A chosen value that CREATED the property changed the collection's
        // shape — a status nothing carried before can group a board that had
        // no groups. Same rule as a text write, and the same reason.
        const patch = outcome.commit.patches.find(
          (p) => p.object === anchor.object && p.role === res.intent.role,
        );
        requestRepaint(patch?.created ? 'structure' : 'value');
      });
  };

  const onBeforeInput = (e: Event): void => {
    const el = e.target as Element | null;
    if (!isEditable(el) || foreign(el) || !pass) return;
    const inputType = (e as InputEvent).inputType ?? '';

    if (policy.ownUndo && inputType.startsWith('history')) {
      // Cancelled, not performed: the chord is already handled on keydown, and
      // running it here as well would undo twice on any browser where both
      // fire.
      e.preventDefault();
      return;
    }

    const anchor = readAnchor(el);
    if (!anchor) {
      // Editable, inside the render root, and carrying no anchor: a renderer
      // that emitted an editing affordance with nothing behind it. Refusing is
      // the only answer that does not write somewhere unknown.
      e.preventDefault();
      announce(refuse('no-grant'));
      return;
    }
    if (!live || live.el !== el) openSession(el, anchor);

    const permitted = permitGesture({
      ir: pass.ir, grants: pass.grants, anchor, epoch: pass.epoch,
      gesture: { kind: 'text', inputType, text: textOf(el) },
    });
    if (!permitted.ok) {
      // The first of three refusals, and the only one the user never has to see
      // corrected: the character is not typed, so the screen never showed a
      // value that was not saved.
      e.preventDefault();
      announce(permitted.refusal);
      if (live?.el === el) dropSession();
      return;
    }
    if (live?.el === el) live.dirty = true;
  };

  const onCompositionStart = (): void => { if (live) live.composing = true; };
  const onCompositionEnd = (): void => {
    if (!live) return;
    live.composing = false;
    live.dirty = true;
  };

  const onKeyDown = (e: Event): void => {
    // Undo included. A foreign surface owns its own history, and Ctrl+Z here
    // would undo a store commit the person made somewhere else entirely.
    if (foreign(e.target as Element | null)) return;
    const k = e as KeyboardEvent;
    const mod = k.ctrlKey || k.metaKey;
    const key = k.key?.toLowerCase();

    if (policy.ownUndo && mod && key === 'z') {
      e.preventDefault();
      void (k.shiftKey ? redo() : undo());
      return;
    }
    if (policy.ownUndo && mod && key === 'y') {
      e.preventDefault();
      void redo();
      return;
    }
    if (!live) return;
    // Enter ends the edit — unless the field holds PROSE, where it starts a
    // paragraph and the browser's own default is exactly right.
    //
    // This was unconditional, and it is why a note could not be written. Every
    // Enter in a body committed the field, so no line break was ever created:
    // two paragraphs typed in sequence arrived in the store as one run-on
    // string with the words at the boundary jammed together, because the second
    // session simply appended to the first. Measured, not deduced — the DOM
    // showed no <div> and no <br> at any point.
    //
    // The rule lives in `@morph/spec` and not here. Which roles a person writes
    // paragraphs into is a vocabulary claim, the same kind of claim as which
    // roles hold numbers, and a renderer or a host inventing its own list is
    // how two of them come to disagree.
    //
    // Shift+Enter already fell through to the browser, so a break was reachable
    // for anyone who knew — which is the shape of a workaround, not a feature.
    const enterEndsIt = !prose(live) && policy.commitOn.includes('enter');
    if (key === 'enter' && !k.shiftKey && enterEndsIt) {
      e.preventDefault();
      void commit('enter');
      return;
    }
    if (key === 'escape' && policy.cancelOn.includes('escape')) {
      e.preventDefault();
      const s = live;
      dropSession();
      revert(s);
    }
  };

  root.addEventListener('focusin', onFocusIn);
  root.addEventListener('focusout', onFocusOut);
  root.addEventListener('input', onInput);
  root.addEventListener('beforeinput', onBeforeInput);
  root.addEventListener('change', onChange);
  root.addEventListener('compositionstart', onCompositionStart);
  root.addEventListener('compositionend', onCompositionEnd);
  // Capture, because a browser that does not fire the chord cancelably on
  // beforeinput still has to be stopped before its default action runs.
  root.addEventListener('keydown', onKeyDown, true);

  /* ----------------------------------------------------------------- undo */

  async function undo(): Promise<WriteOutcome | undefined> {
    // An open field session is its own transaction, so it lands in the ledger
    // before the undo walks past it. Otherwise Ctrl+Z would skip the edit the
    // user can currently see.
    if (live) await commit();
    const out = await store.undo(focusedScope());
    if (out && !out.ok) announce(out.refusal);
    if (out?.ok) requestRepaint('structure');
    return out;
  }

  async function redo(): Promise<WriteOutcome | undefined> {
    if (live) await commit();
    const out = await store.redo(focusedScope());
    if (out && !out.ok) announce(out.refusal);
    if (out?.ok) requestRepaint('structure');
    return out;
  }

  /* -------------------------------------------------------------- the API */

  return {
    /** How the previous session ended, read once — task 0163. The demo's
     *  focus-restore routing consumes it to choose between end-of-value (a
     *  completed Enter) and the recorded caret (typing a repaint interrupted).
     *  Read-and-clear, so a later write with no session cannot inherit a
     *  stale record from an earlier cell. */
    takeLastEnded(): SessionEnd | undefined {
      const out = lastEnded;
      lastEnded = undefined;
      return out;
    },
    bind(next: RenderPass): void {
      // The app repainted anyway. `deferRepaintWhileEditing` is asked for, not
      // taken, so the session's element may already be detached — and a commit
      // onto a detached node would land on whatever object now occupies that
      // place.
      if (live) live = undefined;
      if (pass && pass.lens !== next.lens) store.uninstallLens(pass.lens);
      store.installLens(next.lens, next.grants);
      pass = next;
      values = new Map();
      // Every set the pass painted. Indexing only the subject left an editable
      // field in a sourced collection with no revert target.
      for (const set of [next.objects, ...Object.values(next.sources ?? {})]) {
        for (const o of set ?? []) {
          const h = handleOf(o);
          if (h !== undefined) values.set(h, o);
        }
      }
      maybeFlush();
    },
    get epoch(): number { return pass?.epoch ?? -1; },
    isQuiescent,
    session(): FieldSession | undefined {
      return live
        ? {
            anchor: live.anchor, grant: live.grant, startedAt: live.startedAt,
            opening: live.opening, composing: live.composing,
          }
        : undefined;
    },
    /**
     * Put the caret back after a repaint.
     *
     * By `(object, node)` first — and then, when the anchor named a role, by
     * `(object, role)`, because the node index is not stable across the repaint
     * this is being called after. A value write changes the collection's
     * coverage, the generator re-ranks the roles, and the index the caret came
     * from names a different field on the other side. Matching on the index
     * alone therefore SUCCEEDED and put the caret in the wrong cell, which is
     * worse than failing: the edit had landed, the value was right, and only
     * the caret was somewhere nobody asked for.
     *
     * The role of a candidate is taken from the CURRENT pass's IR, never from
     * the DOM — `readAnchor` does not read a role and must not, since a DOM
     * that named its own role could name one it was not granted. The anchor's
     * role is a hint about what to look for; the IR still says what each node
     * is.
     *
     * Ambiguity refuses. Two elements can legitimately name the same object and
     * the same role — a docked panel anchored on the first object of the pool
     * draws that object's fields beside the row that also draws them — and
     * there is nothing in an anchor that tells them apart. Guessing sends the
     * caret to the other surface, which is the same silent wrongness this whole
     * change exists to remove, so an ambiguous fallback leaves focus alone.
     * That is this method's existing rule for an object that went off screen,
     * applied to the case where it is on screen twice.
     */
    restore(anchor: EditAnchor, offset: number): boolean {
      const irRole = (node: number): string | undefined => pass?.ir.nodes[node]?.role;
      const put = (el: Element): true => {
        if (typeof (el as HTMLElement).focus === 'function') (el as HTMLElement).focus();
        caretTo(el, offset);
        return true;
      };

      const sel = `[${ANCHOR_ATTR.node}="${anchor.node}"]`;
      for (const el of Array.from(root.querySelectorAll(sel))) {
        const found = readAnchor(el);
        if (!found || found.object !== anchor.object) continue;
        // The index survived the repaint but now names something else.
        if (anchor.role !== undefined && irRole(found.node) !== anchor.role) continue;
        return put(el);
      }

      if (anchor.role === undefined) return false;

      let only: Element | undefined;
      for (const el of Array.from(root.querySelectorAll(`[${ANCHOR_ATTR.node}]`))) {
        const found = readAnchor(el);
        if (!found || found.object !== anchor.object) continue;
        if (irRole(found.node) !== anchor.role) continue;
        if (only !== undefined) return false;          // two surfaces: refuse to guess
        only = el;
      }
      return only === undefined ? false : put(only);
    },
    undo,
    redo,
    notify(why: RepaintReason): void { requestRepaint(why); },
    destroy(): void {
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      root.removeEventListener('input', onInput);
      root.removeEventListener('beforeinput', onBeforeInput);
      root.removeEventListener('change', onChange);
      root.removeEventListener('compositionstart', onCompositionStart);
      root.removeEventListener('compositionend', onCompositionEnd);
      root.removeEventListener('keydown', onKeyDown, true);
      if (timer !== undefined) clearTimeout(timer);
      timer = undefined;
      live = undefined;
      if (pass) store.uninstallLens(pass.lens);
      pass = undefined;
    },
  };
}
